import { Plus, type LucideIcon } from "lucide-react";
import { Slot } from "radix-ui";
import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIslandContainer } from "@/contexts/island-container";
import { cn } from "@/lib/utils";

import { EmojiPicker } from "./emoji-picker";

// Port of the reference waba-options-menu.tsx (WabaOptionsMenu/WabaContextMenu)
// with the --wa-* palette swapped for the app's tokens: a dropdown menu, a
// cursor-anchored right-click context menu, and the optional WhatsApp-style
// quick-reaction pill floating above either one.

export type MenuOption =
  | {
      type: "item";
      label: ReactNode;
      icon?: LucideIcon;
      iconClassName?: string;
      onClick: () => void;
      // If set, focus is handed to this callback once the menu finishes
      // closing, instead of being restored to the trigger.
      focusOnClose?: () => void;
    }
  | { type: "separator" };

type FocusOnCloseRef = React.RefObject<(() => void) | null>;

function renderOptions(options: MenuOption[], focusOnClose: FocusOnCloseRef) {
  return options.map((option, index) => {
    if (option.type === "separator") {
      return <DropdownMenuSeparator key={index} />;
    }
    const Icon = option.icon;
    return (
      <DropdownMenuItem
        key={index}
        onClick={() => {
          focusOnClose.current = option.focusOnClose ?? null;
          option.onClick();
        }}
      >
        {Icon && (
          <Icon
            className={cn(
              "tw:size-4.5 tw:text-foreground",
              option.iconClassName,
            )}
            strokeWidth={1.75}
          />
        )}
        {option.label}
      </DropdownMenuItem>
    );
  });
}

// WhatsApp-style quick-reaction row, rendered as a floating pill above the
// menu. Absolute child: its host DropdownMenuContent needs `tw:relative` plus
// `overflow: visible`.
function ReactionBar({
  emojis,
  reactions,
  onReact,
  onClose,
}: {
  emojis: string[];
  /** Emojis the driver has placed on this message (one today). */
  reactions: string[];
  onReact: (emoji: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="tw:absolute tw:bottom-full tw:left-0 tw:mb-2 tw:flex tw:items-center tw:gap-0.5 tw:rounded-full tw:border tw:border-border tw:bg-popover tw:px-1.5 tw:py-1 tw:shadow-md tw:animate-in tw:fade-in-0 tw:zoom-in-95">
      {emojis.map((emoji) => {
        const selected = reactions.includes(emoji);
        return (
          <button
            key={emoji}
            type="button"
            // Keep Radix from treating this as an outside dismiss before the
            // click lands, then react and close ourselves.
            onPointerDown={(event) => event.preventDefault()}
            // One reaction per driver: a new emoji replaces it, picking the
            // selected one (empty emoji) removes it.
            onClick={() => {
              onReact(selected ? "" : emoji);
              onClose();
            }}
            className={cn(
              "tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-full tw:text-2xl tw:leading-none tw:hover:bg-muted",
              selected && "tw:bg-muted",
            )}
          >
            {emoji}
          </button>
        );
      })}
      <EmojiPicker
        onSelect={(emoji) => {
          onReact(emoji);
          onClose();
        }}
      >
        <button
          type="button"
          className="tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:hover:bg-muted"
        >
          <Plus className="tw:size-5" strokeWidth={2} />
        </button>
      </EmojiPicker>
    </div>
  );
}

export function OptionsMenu({
  options,
  align = "end",
  children,
  reactionEmojis,
  reactions,
  onReact,
}: {
  options: MenuOption[];
  align?: "start" | "center" | "end";
  children: ReactNode;
  reactionEmojis?: string[];
  reactions?: string[];
  onReact?: (emoji: string) => void;
}) {
  const focusOnClose = useRef<(() => void) | null>(null);
  // Controlled so the reaction pill can close the menu after a pick.
  const [open, setOpen] = useState(false);

  const showReactions = !!(onReact && reactionEmojis && reactionEmojis.length);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="tw:relative tw:w-48 tw:rounded-xl tw:text-sm"
        style={showReactions ? { overflow: "visible" } : undefined}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const focus = focusOnClose.current;
          focusOnClose.current = null;
          focus?.();
        }}
      >
        {showReactions ? (
          <ReactionBar
            emojis={reactionEmojis!}
            reactions={reactions ?? []}
            onReact={onReact!}
            onClose={() => setOpen(false)}
          />
        ) : null}
        {renderOptions(options, focusOnClose)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Opens the same options when the wrapped children are right-clicked.
// Deliberately mouse-only: touch long-press has its own affordances, so
// non-mouse contextmenu events (e.g. Android long-press) pass through. The
// menu anchors to an invisible element parked at the cursor.
export function AppContextMenu({
  options,
  children,
  reactionEmojis,
  reactions,
  onReact,
}: {
  options: MenuOption[];
  children: ReactNode;
  reactionEmojis?: string[];
  reactions?: string[];
  onReact?: (emoji: string) => void;
}) {
  const focusOnClose = useRef<(() => void) | null>(null);
  const lastPointerType = useRef<string>("mouse");
  const [open, setOpen] = useState(false);
  const [point, setPoint] = useState({ x: 0, y: 0 });
  // Portal the anchor next to the menu content so its `position: fixed`
  // resolves against the same coordinate space even under transformed
  // ancestors (e.g. the swipe-back translation).
  const { container } = useIslandContainer();

  const showReactions = !!(onReact && reactionEmojis && reactionEmojis.length);

  const anchor = (
    <DropdownMenuTrigger asChild>
      <span
        aria-hidden
        className="tw:pointer-events-none tw:fixed tw:h-0 tw:w-0"
        style={{ left: point.x, top: point.y }}
      />
    </DropdownMenuTrigger>
  );

  return (
    <>
      <Slot.Root
        onPointerDown={(event: React.PointerEvent) => {
          lastPointerType.current = event.pointerType;
        }}
        onContextMenu={(event: React.MouseEvent) => {
          if (lastPointerType.current !== "mouse") return;
          event.preventDefault();
          event.stopPropagation();
          setPoint({ x: event.clientX, y: event.clientY });
          setOpen(true);
        }}
      >
        {children}
      </Slot.Root>
      {/* Non-modal so a second right-click passes through to the underlying
          element instead of being swallowed by a full-viewport guard. */}
      <DropdownMenu
        // Radix anchors once on open; key on the point so a right-click at a
        // new spot re-anchors the menu at the latest cursor position.
        key={`${point.x}:${point.y}`}
        open={open}
        onOpenChange={setOpen}
        modal={false}
      >
        {container ? createPortal(anchor, container) : anchor}
        <DropdownMenuContent
          align="start"
          className="tw:relative tw:w-48 tw:rounded-xl tw:text-sm"
          style={showReactions ? { overflow: "visible" } : undefined}
          onContextMenu={(event) => {
            event.preventDefault();
            setPoint({ x: event.clientX, y: event.clientY });
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const focus = focusOnClose.current;
            focusOnClose.current = null;
            focus?.();
          }}
        >
          {showReactions ? (
            <ReactionBar
              emojis={reactionEmojis!}
              reactions={reactions ?? []}
              onReact={onReact!}
              onClose={() => setOpen(false)}
            />
          ) : null}
          {renderOptions(options, focusOnClose)}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
